import React from 'react';
import LegalPage from './Legalpage';

// Update this date every time you change the text below
const UPDATED = 'October 7, 2026';
const BRAND = 'RoboMind Academy'; // use your registered business name if different

const SECTIONS = [
  {
    id: 'acceptance',
    title: 'Accepting these terms',
    blocks: [
      `By using the ${BRAND} website or booking a class, you agree to these terms. If you do not agree, please do not use our services.`,
      'Our classes are for children and teens. A parent or legal guardian must make bookings and payments and accepts these terms on the child’s behalf.',
    ],
  },
  {
    id: 'classes',
    title: 'Our classes',
    blocks: [
      'We teach live, one-to-one online classes in coding, AI and robotics, for ages 6–17. Classes take place on Zoom and last about 50 minutes.',
      'Courses are sold in packages of sessions, as shown on our pricing page. Class times are agreed between the family and the teacher.',
      'To join a class, the student needs a computer (or an iPad with an external mouse), a stable internet connection, and Zoom installed.',
    ],
  },
  {
    id: 'free-trial',
    title: 'Free trial class',
    blocks: [
      'Each new student can book one free trial class. It is free and there is no obligation to enroll afterwards.',
      'After booking, we send a class code by WhatsApp or email. The code is personal and works for the booked class only.',
      'If a trial time becomes unavailable, we will contact you to choose another time.',
    ],
  },
  // ---- Fill these three when your policies are final (empty = hidden on the page) ----
  {
    id: 'payments',
    title: 'Payments and packages',
    blocks: [
      // e.g. 'Packages are paid in advance, before the first session of the package.',
      // e.g. 'Payment methods: ...',
      // e.g. 'A package must be used within ... months.',
    ],
  },
  {
    id: 'missed-sessions',
    title: 'Rescheduling and missed sessions',
    blocks: [
      // e.g. 'Please tell your teacher at least ... hours before a class to reschedule.',
      // e.g. 'Sessions missed without notice are counted as completed.',
    ],
  },
  {
    id: 'refunds',
    title: 'Refunds',
    blocks: [
      // e.g. 'Unused sessions can be refunded within ... days of payment.',
    ],
  },
  // -------------------------------------------------------------------------------------
  {
    id: 'accounts',
    title: 'Student and teacher accounts',
    blocks: [
      'Enrolled students and teachers receive a username and password. Keep them private and do not share your account.',
      'Tell us straight away if you think someone else is using your account. We may suspend an account that is misused.',
    ],
  },
  {
    id: 'conduct',
    title: 'Behaviour in class',
    blocks: [
      'We want every class to be safe and respectful. Students, parents and teachers agree to:',
      [
        'Be respectful and kind to each other',
        'Not record, screenshot or share a class without permission from both the family and RoboMind',
        'Not share personal contact details or links with others during class',
      ],
      'A teacher may end a session early if behaviour is unsafe or abusive. For younger children, we recommend that an adult stays nearby during class.',
    ],
  },
  {
    id: 'student-work',
    title: 'Student projects and achievements',
    blocks: [
      'Projects that students make in class belong to the student.',
      'With the family’s agreement, we may show a student’s first name and last initial, course, completed classes and certificates on our website. We never show full names. A parent can ask us to remove this at any time.',
    ],
  },
  {
    id: 'website-use',
    title: 'Using our website',
    blocks: [
      'You may use our website and materials for personal, educational, non-commercial purposes. You agree not to:',
      [
        'Copy, change or share our website content or course materials without permission',
        'Collect data from the website by scraping or other automated means',
        'Use the website for anything unlawful',
        'Try to access our systems or other people’s accounts without permission',
      ],
    ],
  },
  {
    id: 'ip',
    title: 'Our content',
    blocks: [
      `All website content, course materials, slides, projects we provide, logos and designs belong to ${BRAND} and are protected by copyright. Tools and brands we teach with (for example Scratch, Python, Minecraft or LEGO) belong to their owners.`,
    ],
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer and liability',
    blocks: [
      'We work hard to give accurate information and good classes, but our website and services are provided “as is”. Every child learns at their own pace, so we cannot promise specific results.',
      `To the extent allowed by law, ${BRAND} is not liable for indirect or consequential losses arising from use of the website or services, including internet or device problems on the family’s side.`,
    ],
  },
  {
    id: 'links',
    title: 'Links to other websites',
    blocks: [
      'Our website may link to other sites such as Zoom, WhatsApp or STEM.org. We are not responsible for their content or privacy practices.',
    ],
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    blocks: [
      'We may update these terms. The date at the top shows the latest version. If we make an important change, we will tell enrolled families.',
    ],
  },
  {
    id: 'law',
    title: 'Governing law',
    blocks: [
      'These terms are governed by the laws of Lebanon. Any dispute will be handled by the competent courts of Lebanon.',
    ],
  },
];

const TermsOfService = () => (
  <LegalPage
    eyebrow="Legal"
    title="Terms of Service"
    updated={UPDATED}
    intro={`These terms explain how ${BRAND} classes and this website work. We kept them as short and clear as we could.`}
    summary={[
      'Live one-to-one online classes on Zoom, for ages 6–17.',
      'A parent or guardian books and pays on behalf of the child.',
      'The first trial class is free, with no obligation.',
      'Classes must stay safe and respectful. No recording without permission.',
    ]}
    sections={SECTIONS}
    related={{ to: '/privacy', label: 'Read our Privacy Policy' }}
  />
);

export default TermsOfService;